/**
 * Batch-7 B4 — aanbiedingen aan kandidaten.
 *
 * Verzenden genereert een aanbiedingsbrief-PDF via het bestaande
 * onboarding-template-systeem (templatetype AANBIEDINGSBRIEF), verstuurt een
 * mail (MailService, brief als bijlage), schrijft een notitie (type AANBOD)
 * en plant een opvolgreminder op geldigTot - X dagen. Status kandidaat →
 * AANBIEDING_GEDAAN.
 */
import { z } from 'zod'
import { idParam } from './id-validators'
// batch-62 briefpapier (FO-459) - de briefpapierkeuze bij generatie.
import { BriefpapierKeuzeInvoerSchema } from './briefpapier'

export const KANDIDAAT_AANBIEDING_STATUS = [
  'CONCEPT',
  'VERZONDEN',
  'GEACCEPTEERD',
  'AFGEWEZEN',
  'VERLOPEN',
] as const
export type KandidaatAanbiedingStatus = (typeof KANDIDAAT_AANBIEDING_STATUS)[number]

export const KANDIDAAT_AANBIEDING_STATUS_LABELS: Record<
  KandidaatAanbiedingStatus,
  string
> = {
  CONCEPT: 'Concept',
  VERZONDEN: 'Verzonden',
  GEACCEPTEERD: 'Geaccepteerd',
  AFGEWEZEN: 'Niet geaccepteerd',
  VERLOPEN: 'Verlopen',
}

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als yyyy-MM-dd')

export const createKandidaatAanbiedingSchema = z.object({
  functie: z.string().trim().min(1).max(150),
  startdatum: isoDate.nullable().optional(),
  /** Bruto maandsalaris van het aanbod (Decimal in de database). */
  salaris: z.number().min(0).max(99_999_999),
  contracturen: z.number().min(1).max(80),
  contractperiode: z.string().trim().max(100).nullable().optional(),
  proeftijd: z.string().trim().max(100).nullable().optional(),
  entityId: idParam('entityId').nullable().optional(),
  standplaats: z.string().trim().max(150).nullable().optional(),
  autoVanDeZaak: z.boolean().optional(),
  telefoonVanDeZaak: z.boolean().optional(),
  gereedschap: z.boolean().optional(),
  ploegendienst: z.boolean().optional(),
  bijzonderheden: z.string().trim().max(4000).nullable().optional(),
  geldigTot: isoDate.nullable().optional(),
  /** FO-459 (batch-62) - briefpapierkeuze voor de aanbiedingsbrief; leeg = cascade. */
  briefpapier: BriefpapierKeuzeInvoerSchema.nullable().optional(),
  /** FO-517 §I20 - opleidingsafspraken bij het aanbod. */
  opleidingsafspraken: z.string().trim().max(4000).nullable().optional(),
})
export type CreateKandidaatAanbiedingInput = z.infer<
  typeof createKandidaatAanbiedingSchema
>

export const updateKandidaatAanbiedingSchema =
  createKandidaatAanbiedingSchema.partial()
export type UpdateKandidaatAanbiedingInput = z.infer<
  typeof updateKandidaatAanbiedingSchema
>

export const afwijsAanbiedingSchema = z.object({
  toelichting: z.string().trim().max(2000).nullable().optional(),
  tegenvoorstel: z.boolean().optional(),
  /** Optioneel opvolgmoment (zet kandidaat.volgendeActieOp). */
  opvolgDatum: isoDate.nullable().optional(),
  /** Kandidaat naar de talentpool i.p.v. NIET_GEACCEPTEERD laten staan. */
  naarTalentpool: z.boolean().optional(),
})
export type AfwijsAanbiedingInput = z.infer<typeof afwijsAanbiedingSchema>

export interface KandidaatAanbiedingDTO {
  id: string
  kandidaatId: string
  kandidaatNaam: string
  functie: string
  startdatum: string | null
  salaris: number
  contracturen: number
  contractperiode: string | null
  proeftijd: string | null
  entityId: string | null
  entityNaam: string | null
  standplaats: string | null
  autoVanDeZaak: boolean
  telefoonVanDeZaak: boolean
  gereedschap: boolean
  ploegendienst: boolean
  bijzonderheden: string | null
  geldigTot: string | null
  status: KandidaatAanbiedingStatus
  verzondenOp: string | null
  beslistOp: string | null
  afwijsToelichting: string | null
  tegenvoorstel: boolean
  documentId: string | null
  // FO-517 §I20 + §H19 - opleidingsafspraken en de salarisband-check.
  opleidingsafspraken: string | null
  /** Valt het salaris buiten de band van de actuele salarisindicatie? */
  buitenBand: boolean
  bandOnderbouwing: string | null
  bandGoedgekeurdOp: string | null
  createdAt: string
  updatedAt: string
}
